import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useOutletContext } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import PhotoCamera from '@mui/icons-material/PhotoCamera'
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Portal,
  Radio,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import { FieldArray, Form, Formik } from 'formik'
import {
  assign,
  compact,
  filter,
  find,
  flatMap,
  get,
  includes,
  isEmpty,
  isNull,
  map,
  some,
  spread,
} from 'lodash'

import { Dictionaries, InteriorCondition } from '../api/index'
import ButtonBox from '../components/ButtonBox'
import ImageModal from '../components/ImageModal'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import useAlert from '../hooks/useAlert'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import {
  ArrowLeft,
  ArrowRight,
  CancelIcon,
  CheckIcon,
  DeleteIcon,
  InteriorIcon,
  SaveIcon,
} from '../icons'

const isOptionSelected = (excludeItems, value) => {
  const excludeIds = compact(
    map(excludeItems, (item) =>
      item.interiorDamageId !== 1 ? item.interiorDamageId : null,
    ),
  )
  return includes(excludeIds, value)
}

const mergeByProperty = (target, source, prop) =>
  source.map((sourceElement) => {
    const targetElement = find(target, (t) => sourceElement[prop] === t[prop])
    return targetElement
      ? assign(targetElement, sourceElement)
      : {
          ...sourceElement,
          interiorDamages: [],
        }
  })

function ControlsEditInteriorCondition() {
  const {
    controlId: digitalCertificateId,
    portalRef,
    onCancelClick,
  } = useOutletContext()
  const { t } = useTranslation()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const { setAlert } = useAlert()
  const [interiorDamageDictionary, setInteriorDamageDictionary] = useState([])
  const [initialValues, setInitialValues] = useState({
    digitalCertificateId: '',
    interiorConditions: [],
  })
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    title: '',
  })

  useEffect(() => {
    Promise.all([
      Dictionaries.getInteriorElementsDictionary(axiosPrivate),
      Dictionaries.getInteriorDamageDictionary(axiosPrivate),
      InteriorCondition.getInteriorCondition(
        axiosPrivate,
        digitalCertificateId,
        axiosPrivate,
      ),
    ])
      .then(
        spread((_elements, _damages, _values) => {
          const newElements = map(_elements.data, ({ name, value }) => ({
            interiorElementName: name,
            interiorElementId: value,
          }))

          setInteriorDamageDictionary(_damages.data)
          setInitialValues({
            digitalCertificateId,
            interiorConditions: map(
              mergeByProperty(_values.data, newElements, 'interiorElementId'),
              (elem) => ({
                ...elem,
                interiorDamages: isEmpty(elem.interiorDamages)
                  ? [{ interiorDamageId: 1, isMain: true, image: null }]
                  : elem.interiorDamages,
              }),
            ),
          })
        }),
      )
      // eslint-disable-next-line no-console
      .catch((e) => console.error(e))
  }, [axiosPrivate, digitalCertificateId])

  const checkImagesWithNoDesc = (values) => {
    const imagesWithNoDescArr = map(
      values,
      ({ interiorElementName, interiorDamages }) =>
        map(interiorDamages, (damage) => {
          if (
            get(damage, 'interiorDamageId', 1) === 1 &&
            get(damage, 'image', null)
          ) {
            return interiorElementName
          }
          return null
        }),
    )
    const imageWithNoDesc = find(flatMap(imagesWithNoDescArr))

    return imageWithNoDesc
  }

  const filterUnnecessaryDamages = (damages) => {
    const filteredDamages = filter(
      damages,
      (el) => get(el, 'interiorDamageId', 1) !== 1,
    )
    const noDamages = filteredDamages.length === 0

    if (noDamages) {
      return [
        {
          interiorDamageId: 1,
          isMain: true,
          image: null,
        },
      ]
    }

    const hasMainImage = some(
      filteredDamages,
      (el) => get(el, 'isMain') === true,
    )

    if (!hasMainImage) {
      filteredDamages[0].isMain = true
    }

    return filteredDamages
  }

  const saveInteriorCondition = ({
    redirect,
    values,
    submitForm,
    setSubmitting,
  }) => {
    const payload = {
      ...values,
      interiorConditions: map(
        values.interiorConditions,
        ({ interiorDamages, interiorElementId }) => ({
          interiorElementId,
          interiorDamages: map(
            filterUnnecessaryDamages(interiorDamages),
            (damage) => ({
              ...damage,
              imageId: damage.image?.id || null,
            }),
          ),
        }),
      ),
    }

    const imageError = checkImagesWithNoDesc(values.interiorConditions)

    if (imageError) {
      setAlert(
        `Wybierz rodzaj uszkodzenia dla zdjęcia w sekcji ${imageError}`,
        'error',
      )
    } else {
      submitForm()
      InteriorCondition.saveInteriorCondition(
        axiosPrivate,
        digitalCertificateId,
        payload,
      )
        .then(() => {
          if (redirect) {
            navigate(redirect)
          }
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.error(e))
        .finally(() => {
          setSubmitting(false)
        })
    }
  }

  const saveInteriorConditionImage =
    (interiorElementId, setFieldValue) => (e) => {
      const fileForm = new FormData()
      fileForm.append('file', e.target.files[0])
      InteriorCondition.saveInteriorConditionImage(
        axiosPrivate,
        digitalCertificateId,
        interiorElementId,
        fileForm,
      )
        .then(({ data: { data } }) => {
          setFieldValue(e.target.name, data)
        })
        // eslint-disable-next-line no-shadow, no-console
        .catch((e) => console.error(e))
    }

  const deleteInteriorConditionImage = (name, setFieldValue) => () => {
    setFieldValue(name, null)
  }

  const handleImageModalOpen = (title, imageUrl) => () => {
    setImageModal({ isOpen: true, imageUrl, title })
  }

  const handleImageModalClose = () => {
    setImageModal({ isOpen: false, imageUrl: '', title: '' })
  }

  return (
    <div>
      <Section>
        <SectionHeader
          icon={<InteriorIcon />}
          title="Opis stanu wnętrza"
          subtitle="Opisz wszystkie uszkodzenia występujące w środku pojazdu zgodnie z poniższym formularzem. Należy dodać zdjęcie każdego elementu z uszkodzeniem."
        />
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={() => {}}
        >
          {({
            isSubmitting,
            values,
            handleChange,
            setSubmitting,
            setFieldValue,
            submitForm,
          }) => (
            <Form>
              <Box>
                <FieldArray name="interiorConditions">
                  <>
                    {map(values.interiorConditions, (element, elementIndex) => (
                      <Paper
                        key={element.interiorElementId}
                        elevation={0}
                        sx={{ mb: 2, p: 2 }}
                      >
                        <Typography
                          variant="h4"
                          gutterBottom
                          sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            backgroundColor: '#fff',
                          }}
                        >
                          {element.interiorElementName}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <FieldArray
                              name={`interiorConditions[${elementIndex}].interiorDamages`}
                            >
                              {(arrayHelpers) => {
                                const handleMainDamageChange = (e) => {
                                  e.preventDefault()

                                  get(values, arrayHelpers.name).forEach(
                                    (_, key) => {
                                      setFieldValue(
                                        `${arrayHelpers.name}[${key}].isMain`,
                                        false,
                                      )
                                    },
                                  )
                                  setFieldValue(e.target.name, true)
                                }
                                const handleDamageRemove = (index) => () => {
                                  arrayHelpers.remove(index)
                                  const interiorDamagesArray = get(
                                    values,
                                    arrayHelpers.name,
                                  )
                                  const deletedItem =
                                    interiorDamagesArray[index]
                                  if (interiorDamagesArray.length === 1) {
                                    arrayHelpers.push({
                                      interiorDamageId: 1,
                                      isMain: true,
                                      image: null,
                                    })
                                  } else {
                                    const newInteriorDamages = filter(
                                      interiorDamagesArray,
                                      (o) =>
                                        o.interiorDamageId !==
                                        deletedItem.interiorDamageId,
                                    )
                                    if (
                                      !find(newInteriorDamages, {
                                        isMain: true,
                                      })
                                    ) {
                                      setFieldValue(
                                        arrayHelpers.name,
                                        map(newInteriorDamages, (damage, key) =>
                                          key === 0
                                            ? { ...damage, isMain: true }
                                            : damage,
                                        ),
                                      )
                                    }
                                  }
                                }

                                return (
                                  <>
                                    <Box>
                                      {map(
                                        values.interiorConditions[elementIndex]
                                          .interiorDamages,
                                        (damage, damageIndex) => (
                                          <Grid
                                            container
                                            key={damageIndex}
                                            sx={{
                                              mt: damageIndex > 0 && 4,
                                            }}
                                          >
                                            <Grid item xs={12} md={7}>
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                              >
                                                {t('carEditDamage')}
                                              </Typography>
                                              <Box sx={{ display: 'flex' }}>
                                                <Select
                                                  name={`interiorConditions[${elementIndex}].interiorDamages[${damageIndex}].interiorDamageId`}
                                                  value={
                                                    damage.interiorDamageId
                                                  }
                                                  fullWidth
                                                  onChange={handleChange}
                                                >
                                                  {map(
                                                    interiorDamageDictionary,
                                                    ({ name, value }, key) => (
                                                      <MenuItem
                                                        key={key}
                                                        value={value}
                                                        disabled={isOptionSelected(
                                                          values
                                                            .interiorConditions[
                                                            elementIndex
                                                          ].interiorDamages,
                                                          value,
                                                        )}
                                                        sx={{
                                                          display:
                                                            value === 1
                                                              ? 'none'
                                                              : 'flex',
                                                        }}
                                                      >
                                                        {name}
                                                      </MenuItem>
                                                    ),
                                                  )}
                                                </Select>
                                                <Tooltip
                                                  arrow
                                                  title="Usuń uszkodzenie"
                                                >
                                                  <IconButton
                                                    disableFocusRipple
                                                    disableRipple
                                                    size="small"
                                                    onClick={handleDamageRemove(
                                                      damageIndex,
                                                    )}
                                                    sx={({ palette }) => ({
                                                      borderRadius: 0,
                                                      color:
                                                        palette.primary
                                                          .contrastText,
                                                      backgroundColor:
                                                        palette.primary.main,
                                                    })}
                                                  >
                                                    <ClearIcon />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </Grid>
                                            <Grid
                                              item
                                              xs={12}
                                              md={2}
                                              sx={{
                                                display: {
                                                  xs: 'block',
                                                  md: 'flex',
                                                },
                                                justifyContent: 'center',
                                                mt: {
                                                  xs: 2,
                                                  md: 0,
                                                },
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                                sx={{
                                                  display: {
                                                    xs: 'block',
                                                    md: 'none',
                                                  },
                                                }}
                                              >
                                                {t(
                                                  'Wybierz jako główne uszkodzenie do oceny',
                                                )}
                                              </Typography>
                                              <Box sx={{ display: 'flex' }}>
                                                <Tooltip
                                                  arrow
                                                  title="Wybierz jako główne uszkodzenie do oceny"
                                                >
                                                  <FormControl
                                                    sx={{
                                                      justifyContent: 'center',
                                                      mt: {
                                                        md: 2,
                                                      },
                                                    }}
                                                  >
                                                    <Radio
                                                      name={`interiorConditions[${elementIndex}].interiorDamages[${damageIndex}].isMain`}
                                                      value={
                                                        values
                                                          .interiorConditions[
                                                          elementIndex
                                                        ].interiorDamages[
                                                          damageIndex
                                                        ].isMain
                                                      }
                                                      checked={
                                                        values
                                                          .interiorConditions[
                                                          elementIndex
                                                        ].interiorDamages[
                                                          damageIndex
                                                        ].isMain
                                                      }
                                                      sx={{ py: 1, px: 2 }}
                                                      onChange={
                                                        handleMainDamageChange
                                                      }
                                                    />
                                                  </FormControl>
                                                </Tooltip>
                                              </Box>
                                            </Grid>
                                            <Grid item xs={12} md={3}>
                                              <Typography
                                                variant="caption"
                                                gutterBottom
                                              >
                                                {t('carEditDamagePhoto')}
                                              </Typography>
                                              <Box>
                                                {isNull(
                                                  values.interiorConditions[
                                                    elementIndex
                                                  ].interiorDamages[damageIndex]
                                                    .image,
                                                ) ? (
                                                  <Button
                                                    variant="contained"
                                                    component="label"
                                                    startIcon={<PhotoCamera />}
                                                    fullWidth
                                                    sx={{ height: 56 }}
                                                  >
                                                    <input
                                                      name={`interiorConditions[${elementIndex}].interiorDamages[${damageIndex}].image`}
                                                      hidden
                                                      accept="image/*"
                                                      type="file"
                                                      onChange={saveInteriorConditionImage(
                                                        element.interiorElementId,
                                                        setFieldValue,
                                                      )}
                                                      style={{
                                                        display: 'none',
                                                      }}
                                                    />
                                                  </Button>
                                                ) : (
                                                  <Box sx={{ display: 'flex' }}>
                                                    <Button
                                                      fullWidth
                                                      onClick={handleImageModalOpen(
                                                        element.interiorElementName,
                                                        damage.image.imageUrl,
                                                      )}
                                                      sx={{
                                                        p: 0,
                                                        height: 56,
                                                        backgroundImage: `url(${damage.image.imageUrl})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition:
                                                          'center',
                                                      }}
                                                    />
                                                    <Tooltip
                                                      arrow
                                                      title="Usuń zdjęcie"
                                                    >
                                                      <IconButton
                                                        size="small"
                                                        disableFocusRipple
                                                        disableRipple
                                                        sx={({ palette }) => ({
                                                          borderRadius: 0,
                                                          fill: palette.primary
                                                            .contrastText,
                                                          backgroundColor:
                                                            palette.primary
                                                              .main,
                                                        })}
                                                        onClick={deleteInteriorConditionImage(
                                                          `interiorConditions[${elementIndex}].interiorDamages[${damageIndex}].image`,
                                                          setFieldValue,
                                                        )}
                                                      >
                                                        <DeleteIcon />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Box>
                                                )}
                                              </Box>
                                            </Grid>
                                          </Grid>
                                        ),
                                      )}
                                    </Box>
                                    <Button
                                      size="small"
                                      variant="text"
                                      startIcon={<AddIcon fontSize="small" />}
                                      sx={{ mt: 1, p: 1 }}
                                      onClick={() => {
                                        arrayHelpers.push({
                                          interiorDamageId: 1,
                                          isMain: false,
                                          image: null,
                                        })
                                      }}
                                    >
                                      {t('carEditDamageAdd')}
                                    </Button>
                                  </>
                                )
                              }}
                            </FieldArray>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </>
                </FieldArray>
              </Box>
              <Portal container={portalRef.current}>
                <ButtonBox
                  buttons={[
                    {
                      label: 'Wstecz',
                      icon: <ArrowLeft fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveInteriorCondition({
                            redirect: `/controls/${digitalCertificateId}/edit/bodyCondition`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: 'Anuluj',
                      icon: <CancelIcon />,
                      props: {
                        variant: 'outlined',
                        color: 'primary',
                        onClick: onCancelClick,
                      },
                    },
                    {
                      label: 'Zapisz',
                      icon: <SaveIcon fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveInteriorCondition({
                            redirect: false,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: 'Zakończ',
                      icon: <CheckIcon fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveInteriorCondition({
                            redirect: `/controls/${digitalCertificateId}/summary`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                    {
                      label: t('nextBtnLabel'),
                      icon: <ArrowRight fill="white" />,
                      props: {
                        variant: 'contained',
                        color: 'primary',
                        loading: isSubmitting,
                        onClick: () => {
                          saveInteriorCondition({
                            redirect: `/controls/${digitalCertificateId}/edit/equipment`,
                            values,
                            submitForm,
                            setSubmitting,
                          })
                        },
                      },
                    },
                  ]}
                />
              </Portal>
            </Form>
          )}
        </Formik>
      </Section>
      <ImageModal
        title={imageModal.title}
        isOpen={imageModal.isOpen}
        imageUrl={imageModal.imageUrl}
        onClose={handleImageModalClose}
      />
    </div>
  )
}

export default ControlsEditInteriorCondition
